import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'

import cx from 'classnames'

import { RadialEngagementScore } from 'components/EngagementRadialScore'
import { EngagementScoreFlames } from 'components/EngagementScoreFlames'
import { CurrentUserContext } from 'context/currentUserContext'
import { Card, OpenedSidebar, SidebarOpenContext } from 'context/sidebarOpenContext'
import {
  AccountActivity,
  AccountActivityExplain,
  ActivityExplain,
  ExplainTypes
} from 'modules/accountActivity/domain'
import { ReactComponent as SparklesIcon } from 'svgs/sparkles.svg'
import { accountPath, accountsPath } from 'utils/paths'
import { getTimestampActivityFormat } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import ExplainAIEngagement from './ExplainAIEngagement'
import { DEFAULT_TEXT_TEMPLATE, explainTypeToTextTemplatingConfigMap } from './explainConfig'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

interface EngagementScoreProps {
  score: number
  prevScore?: number
  radial?: boolean
  className?: string
}

export const scoreToDescription = (score: number): string => {
  switch (score) {
    case 1:
      return 'Not engaged'
    case 2:
      return 'Somewhat engaged'
    case 3:
      return 'Engaged'
    case 4:
      return 'Very engaged'
    case 5:
      return 'Extremely engaged'
    default:
      return 'Not engaged'
  }
}

const onMoreClick = (history: any) => (activity: AccountActivity) => {
  history.push({
    pathname: accountPath(activity.accountId),
    state: {
      companyData: activity.companyData,
      navigatedFrom: 'Target accounts',
      navigatedFromUrl: accountsPath,
      accountEngagementScore: activity.normalizedEngagementScore
    },
    hash: history.location.hash
  })
}

export const EngagementScoreOverview: React.FC<EngagementScoreProps> = ({
  score,
  prevScore,
  radial = false,
  className
}) => {
  if (score < 0) {
    score = 0
  }

  return (
    <div className={cx(styles.engagementScoreOverviewContainer, className)}>
      {radial ? (
        <RadialEngagementScore score={Math.min(Math.round(score), 5)} prevScore={prevScore} />
      ) : (
        <>
          <div className={cx(styles.engagementScoreNumber, utilityStyles.semiBoldWeight)}>
            {Math.min(Math.round(score), 5)}
          </div>
          <div className={styles.engagementScoreDescriptionContainer}>
            <EngagementScoreFlames score={Math.min(Math.round(score), 5)} />
            <div className={styles.subTextStyle}>
              {scoreToDescription(Math.min(Math.round(score), 5))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export const ExplainRow: React.FC<{ type: ExplainTypes; values: ActivityExplain }> = ({
  type,
  values
}) => {
  const total = values.knownCount + values.unknownCount
  const configStringKey = total > 1 ? 'titleTextPlural' : 'titleText'
  const rowTextConfig = explainTypeToTextTemplatingConfigMap[type] ?? DEFAULT_TEXT_TEMPLATE
  const mainText = `${total} ${rowTextConfig[configStringKey]}`

  const contactsText =
    values.knownCount > 1
      ? `${values.knownCount} contacts`
      : values.knownCount === 1
      ? '1 contact'
      : ''

  const anonymousVisitorsText =
    values.unknownCount > 1
      ? `${values.unknownCount} anonymous visitors`
      : values.unknownCount === 1
      ? '1 anonymous visitor'
      : ''

  const subText = `${rowTextConfig.subTextPrepostion} ${contactsText}${
    anonymousVisitorsText.length > 1 && contactsText.length > 1 ? ',' : ''
  } ${anonymousVisitorsText} • Last ${
    rowTextConfig.mostRecentActionVerb
  } ${getTimestampActivityFormat(values.lastTimestamp)}`
  return (
    <div>
      <div className={cx(styles.explainRowMainText, utilityStyles.semiBoldWeight)}>{mainText}</div>
      <div className={styles.explainRowSubText}>{subText}</div>
    </div>
  )
}

export const ExplainSection: React.FC<{
  accountActivity: AccountActivity
  explainData: AccountActivityExplain
  limit?: number
  displayContextHasBottomBorder?: boolean
  showMore?: boolean
  hasAIScoring?: boolean
}> = ({
  accountActivity,
  explainData,
  limit,
  displayContextHasBottomBorder,
  showMore = true,
  hasAIScoring
}) => {
  const { setOpenedRightSidebar, setExpandedCard } = useContext(SidebarOpenContext)
  const currentUser = useContext(CurrentUserContext)
  const history = useHistory()

  const onClick = onMoreClick(history)

  let data: [ExplainTypes, ActivityExplain][] = explainData
    ? (Object.entries(explainData).sort(
        ([, valueA], [, valueB]) => valueB.totalScore - valueA.totalScore
      ) as [ExplainTypes, ActivityExplain][])
    : []

  data = data.filter(
    ([key]: [ExplainTypes, ActivityExplain]) =>
      explainTypeToTextTemplatingConfigMap[key]?.allowDisplay ?? false
  )

  if (limit) data = data.slice(0, limit)
  return (
    <>
      {accountActivity.aiScoringTopActivities?.length ? (
        <ExplainAIEngagement accountActivity={accountActivity} />
      ) : (
        data.map(([key, value]: [ExplainTypes, ActivityExplain]) => {
          return (
            <div
              key={key}
              className={cx(
                displayContextHasBottomBorder
                  ? styles.borderBottomWithoutLastChild
                  : styles.borderBottom,
                styles.marginTop
              )}>
              <ExplainRow type={key} values={value} />
            </div>
          )
        })
      )}
      {limit && showMore && (
        <div
          className={styles.viewMoreStyle}
          onClick={() => {
            const activityClickEvent = hasAIScoring
              ? TrackType.AI_ENGAGEMENT_SCORE_TOOLTIP_VIEW_ACTIVITY_CLICK
              : TrackType.ENGAGEMENT_SCORE_TOOLTIP_VIEW_ACTIVITY_CLICK
            setExpandedCard(Card.EXPLAIN_CARD)
            setOpenedRightSidebar(OpenedSidebar.DETAILS)
            segmentTrack(activityClickEvent, {
              accountId: accountActivity.accountId,
              orgId: currentUser?.orgId,
              userId: currentUser?.id
            })
            onClick(accountActivity)
          }}
          data-ocweb-id="EngagementScoreFullBreakdown">
          Click to view all activity
        </div>
      )}
    </>
  )
}

export interface ExplainCardProps {
  includeHeader?: boolean
  accountActivity: AccountActivity
  limit?: number
  displayContextHasBottomBorder?: boolean
  showMore?: boolean
}

export const ExplainContents: React.FC<ExplainCardProps> = ({
  accountActivity,
  limit,
  includeHeader = true,
  displayContextHasBottomBorder,
  showMore
}) => {
  const hasAIScoring =
    accountActivity.aiScoringTopActivities?.length &&
    accountActivity.aiScoringTopActivities.length > 1
  const contributingFactorsLabel = `Contributing factors${
    hasAIScoring ? ' determined by Drift AI' : ''
  }`

  return (
    <>
      {includeHeader && (
        <>
          <div className={cx(styles.headerStyle, utilityStyles.semiBoldWeight)}>
            Engagement score
          </div>
          <div className={cx(styles.subTextStyle)}>{accountActivity.companyData?.name ?? ''}</div>
        </>
      )}
      <EngagementScoreOverview score={accountActivity.normalizedEngagementScore} />
      <div className={cx(styles.contributingFactorsStyle, styles.borderBottom)}>
        {hasAIScoring ? <SparklesIcon /> : null} {contributingFactorsLabel}
      </div>
      <ExplainSection
        accountActivity={accountActivity}
        explainData={accountActivity.activityTypeExplain}
        limit={limit}
        displayContextHasBottomBorder={displayContextHasBottomBorder}
        showMore={showMore}
        hasAIScoring={!!hasAIScoring}
      />
    </>
  )
}

export const ExplainHoverCard: React.FC<ExplainCardProps> = props => {
  return (
    <div className={cx(styles.explainCard)} onClick={e => e.stopPropagation()}>
      <ExplainContents {...props} />
    </div>
  )
}

export default ExplainContents
